import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

interface TableRow {
  table_id: number;
  table_row_id: number;
  values: object; // Store row values as JSON
}

interface TableRowState {
  items: TableRow[];
  status: string | null;
  currentRow: TableRow | null;
  currentRowStatus: string | null;
}

const initialState: TableRowState = {
  items: [],
  status: null,
  currentRow: null,
  currentRowStatus: null,
};

// Fetch all rows for a specific table
export const fetchTableRows = createAsyncThunk('tableRow/fetchTableRows', async ({ zone_name, table_name }: { zone_name: string; table_name: string }) => {
  const response = await api.get(`/tables/${zone_name}/${table_name}/rows`);
  return response.data;
});

// Create a new row in a specific table
export const createTableRow = createAsyncThunk(
  'tableRow/createTableRow',
  async ({ zone_name, table_name, values }: { zone_name: string; table_name: string; values: object }) => {
    const response = await api.post(`/tables/${zone_name}/${table_name}/rows`, {
      values,
    });
    return response.data;
  }
);

// Update an existing row in a specific table
export const updateTableRow = createAsyncThunk('tableRow/updateTableRow', async (row: TableRow & { zone_name: string; table_name: string }) => {
  const response = await api.put(`/tables/${row.zone_name}/${row.table_name}/rows/${row.table_row_id}`, row);
  return response.data;
});

// Delete rows in a specific table
export const deleteTableRows = createAsyncThunk(
  'tableRow/deleteTableRows',
  async ({ zone_name, table_name, table_row_ids }: { zone_name: string; table_name: string; table_row_ids: number[] }) => {
    await api.post(`/tables/${zone_name}/${table_name}/rows/delete`, {
      row_ids: table_row_ids,
    });
    return table_row_ids; // Return the array of deleted row IDs
  }
);

// Table Row Slice
const tableRowSlice = createSlice({
  name: 'tableRow',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch all rows for a table
      .addCase(fetchTableRows.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchTableRows.fulfilled, (state, action) => {
        state.items = action.payload;
        state.status = 'succeeded';
      })
      .addCase(fetchTableRows.rejected, (state) => {
        state.status = 'failed';
      })
      // Delete rows - rejected case to handle any errors
      .addCase(deleteTableRows.rejected, (state, action) => {
        state.status = 'failed'; // Set the status to failed
        console.error('Failed to delete rows:', action.error.message); // Log the error message
      });
  },
});

export default tableRowSlice.reducer;
